/**
 * Phase 6d.2 — ContractsImportSection.
 *
 * UI integration for the 6d.1 Excel parser. Surfaces three pieces on
 * the contract list page:
 *
 *   1. Template download button — anchored at
 *      `/templates/IFRS16_Sozlesme_Sablonu.xlsx` (Vite serves
 *      `frontend/public/` at the site root).
 *   2. Drag-and-drop / click-to-select zone for an .xlsx file. Caps at
 *      5 MB and rejects anything not ending in `.xlsx` before invoking
 *      the parser.
 *   3. Summary panel reporting clean / partial / error counts, with a
 *      collapsible details list for the un-parseable rows.
 *
 * Behavioural contract:
 *   • APPEND ONLY. The section reads `engagement.contracts`, appends
 *     mapped `ContractInput` objects, and writes back via the SAME
 *     `historyStorage.saveEngagement` path the manual
 *     `+ Yeni Sözleşme Ekle` button uses (see `ContractListPage`).
 *     Existing rows are never modified or deleted.
 *   • Partial rows (rows with errors but a parseable contractId) are
 *     ALSO pushed into the contracts array with empty-string defaults
 *     for the missing required fields, dirty=true. The existing Zod
 *     schema + status-derivation pipeline marks them `partial`, the
 *     auditor opens each row to fill the gaps — same UX as a manually
 *     added contract that wasn't completed.
 *   • Un-parseable rows (no contractId, header missing, etc.) stay in
 *     the summary panel's collapsible error list and are NOT appended.
 *
 * Feature flag:
 *   `VITE_EXCEL_IMPORT !== "false"` — default-enabled. Returning `null`
 *   when disabled lets the parent mount the section unconditionally
 *   from a single line.
 *
 * Tests use `vi.mock("./excelParser")` so the parser itself isn't
 * exercised here (Phase 6d.1 has its own coverage).
 */

import { ChevronDown, ChevronUp, Download, FileSpreadsheet, Upload, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Admonition } from "@/components/ui/admonition";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { transitionToStale } from "@/features/engagement/engagement-state";
import { cn } from "@/lib/utils";
import { useHistoryStorage } from "@/services";
import type {
  AssetClass,
  ContractInput,
  EngagementHistoryEntry,
  PaymentFrequency,
  PaymentTiming,
  FxApproach as ContractFxApproach,
} from "@/types";

// The parser pulls ExcelJS (~600 KB) into its module graph. Lazy-import
// it inside `handleFile` so the main bundle stays unchanged until the
// auditor actually drops a file. Tests still see the static mock
// because `vi.mock("./excelParser")` is honoured by dynamic imports.
import type {
  ImportResult,
  ImportedContract,
  PartialImportedContract,
} from "./types";

const TEMPLATE_HREF = "/templates/IFRS16_Sozlesme_Sablonu.xlsx";
const EXAMPLE_HREF = "/templates/IFRS16_Ornek_Kilavuz.xlsx";
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB

type DropzoneState = "idle" | "dragOver" | "parsing" | "error";

export interface ContractsImportSectionProps {
  engagement: EngagementHistoryEntry;
  onEngagementUpdated: (updated: EngagementHistoryEntry) => void;
}

function isImportEnabled(): boolean {
  // Read at render time (not module-load) so tests can flip the env
  // via `vi.stubEnv` between renders.
  return import.meta.env.VITE_EXCEL_IMPORT !== "false";
}

export function ContractsImportSection({
  engagement,
  onEngagementUpdated,
}: ContractsImportSectionProps) {
  const { t } = useTranslation();
  const historyStorage = useHistoryStorage();
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const [dropzoneState, setDropzoneState] =
    React.useState<DropzoneState>("idle");
  const [fileError, setFileError] = React.useState<string | null>(null);
  const [summary, setSummary] = React.useState<ImportResult | null>(null);
  const [showErrors, setShowErrors] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);

  if (!isImportEnabled()) {
    return null;
  }

  function clickFilePicker() {
    if (dropzoneState === "parsing") return;
    fileInputRef.current?.click();
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (dropzoneState === "parsing") return;
    setDropzoneState("dragOver");
  }

  function onDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (dropzoneState === "parsing") return;
    setDropzoneState("idle");
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (dropzoneState === "parsing") return;
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset the input value so the same file can be dropped twice
    // back-to-back if the auditor edits + re-imports.
    e.target.value = "";
    if (file) void handleFile(file);
  }

  async function handleFile(file: File) {
    setFileError(null);
    setDismissed(false);

    if (!isXlsxByName(file.name)) {
      setFileError(t("import.dropzone.errorFileType"));
      setDropzoneState("error");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileError(t("import.dropzone.errorFileSize"));
      setDropzoneState("error");
      return;
    }

    setDropzoneState("parsing");
    let parsed: ImportResult;
    try {
      const { parseContractsExcel } = await import("./excelParser");
      parsed = await parseContractsExcel(file);
    } catch (err) {
      // Defensive — the parser itself never throws under normal input,
      // but a corrupted .xlsx may bubble an ExcelJS error.
      // eslint-disable-next-line no-console
      console.error("[contract-import] parser threw", err);
      setFileError(t("import.dropzone.errorFileType"));
      setDropzoneState("error");
      return;
    }

    const appendable = [
      ...parsed.contracts.map(mapCleanToContractInput),
      ...parsed.partialContracts.map(mapPartialToContractInput),
    ];

    if (appendable.length > 0) {
      const nextContracts = [...engagement.contracts, ...appendable];
      const nextDirty = [
        ...(engagement.contractsDirty ?? []),
        ...appendable.map(() => true),
      ];
      let updated: EngagementHistoryEntry = {
        ...engagement,
        contracts: nextContracts,
        contractsDirty: nextDirty,
        contractCount: nextContracts.length,
        updatedAt: new Date().toISOString(),
      };
      updated = transitionToStale(updated);
      try {
        await historyStorage.saveEngagement(updated);
        onEngagementUpdated(updated);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[contract-import] saveEngagement failed", err);
      }
    }

    setSummary(parsed);
    setShowErrors(false);
    setDropzoneState("idle");
  }

  const showSummary = summary !== null && !dismissed;

  return (
    <Card
      className="p-4"
      data-testid="contracts-import-section"
    >
      <div className="flex flex-col gap-3">
        <div>
          <h3 className="text-base font-semibold">
            {t("import.section.title")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("import.section.description")}
          </p>
        </div>

        <Admonition type="note" title={t("import.scopeNotice.title")}>
          <div data-testid="import-scope-notice" className="space-y-1">
            <p>{t("import.scopeNotice.intro")}</p>
            <ul className="list-disc space-y-0.5 pl-5">
              <li>{t("import.scopeNotice.items.modifications")}</li>
              <li>{t("import.scopeNotice.items.termination")}</li>
              <li>{t("import.scopeNotice.items.indexResets")}</li>
              <li>{t("import.scopeNotice.items.priorReconciliation")}</li>
            </ul>
            <p>{t("import.scopeNotice.outro")}</p>
          </div>
        </Admonition>

        <div className="flex flex-col gap-3 md:flex-row md:items-stretch">
          <Button
            asChild
            variant="outline"
            className="md:w-56"
            data-testid="import-download-template"
          >
            <a href={TEMPLATE_HREF} download>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              {t("import.button.downloadTemplate")}
            </a>
          </Button>

          <Button
            asChild
            variant="outline"
            className="md:w-56"
            data-testid="import-download-example"
          >
            <a href={EXAMPLE_HREF} download>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              {t("import.button.downloadExample")}
            </a>
          </Button>

          <div
            role="button"
            tabIndex={dropzoneState === "parsing" ? -1 : 0}
            aria-label={t("import.dropzone.ariaLabel")}
            data-testid="import-dropzone"
            data-state={dropzoneState}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={clickFilePicker}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                clickFilePicker();
              }
            }}
            className={cn(
              "flex flex-1 cursor-pointer items-center justify-center gap-3 rounded-lg border-2 border-dashed px-4 py-6 text-sm transition-colors",
              dropzoneState === "idle" &&
                "border-input hover:border-primary/60",
              dropzoneState === "dragOver" &&
                "border-primary bg-primary/5",
              dropzoneState === "parsing" &&
                "border-primary/40 bg-muted cursor-progress",
              dropzoneState === "error" &&
                "border-destructive bg-destructive/5",
            )}
          >
            {dropzoneState === "parsing" ? (
              <>
                <FileSpreadsheet
                  className="h-6 w-6 animate-pulse text-primary"
                  aria-hidden="true"
                />
                <span className="font-medium" data-testid="import-dropzone-parsing">
                  {t("import.dropzone.parsing")}
                </span>
              </>
            ) : dropzoneState === "error" ? (
              <span
                className="text-destructive text-sm"
                data-testid="import-dropzone-error"
              >
                {fileError}
              </span>
            ) : (
              <>
                <Upload
                  className="h-6 w-6 text-muted-foreground"
                  aria-hidden="true"
                />
                <div className="text-left">
                  <p className="font-medium">
                    {dropzoneState === "dragOver"
                      ? t("import.dropzone.dragOver")
                      : t("import.dropzone.idle")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("import.dropzone.hint")}
                  </p>
                </div>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={onFileInputChange}
              data-testid="import-file-input"
            />
          </div>
        </div>

        {showSummary && summary !== null && (
          <ImportSummaryPanel
            result={summary}
            showErrors={showErrors}
            onToggleErrors={() => setShowErrors((v) => !v)}
            onDismiss={() => setDismissed(true)}
          />
        )}
      </div>
    </Card>
  );
}

// ─── Summary panel ────────────────────────────────────────────────────────

interface ImportSummaryPanelProps {
  result: ImportResult;
  showErrors: boolean;
  onToggleErrors: () => void;
  onDismiss: () => void;
}

function ImportSummaryPanel({
  result,
  showErrors,
  onToggleErrors,
  onDismiss,
}: ImportSummaryPanelProps) {
  const { t } = useTranslation();
  const cleanCount = result.contracts.length;
  const partialCount = result.partialContracts.length;
  // Errors not tied to any partial row — those are the "un-parseable"
  // ones the auditor needs to see in the collapsible details list.
  const unparseableErrors = result.errors.filter((e) => e.contractId === null);
  const errorRowCount = unparseableErrors.length;

  const allFailed = cleanCount === 0 && partialCount === 0;
  const variant: "success" | "warning" | "danger" = allFailed
    ? "danger"
    : partialCount > 0 || errorRowCount > 0
      ? "warning"
      : "success";

  // Re-use the existing Admonition primitive — its three variants map
  // 1:1 onto the spec's green/amber/red states without inventing
  // bespoke styling.
  const admonitionType: "success" | "warning" | "danger" =
    variant === "success" ? "success" : variant === "danger" ? "danger" : "warning";

  return (
    <Admonition
      type={admonitionType}
      className="relative"
    >
      <div className="space-y-2" data-testid="import-summary-panel">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-0.5">
            {allFailed ? (
              <p
                className="font-medium"
                data-testid="import-summary-all-failed"
              >
                {t("import.summary.allFailed")}
              </p>
            ) : (
              <>
                {cleanCount > 0 && (
                  <p data-testid="import-summary-clean">
                    {t("import.summary.clean", { count: cleanCount })}
                  </p>
                )}
                {partialCount > 0 && (
                  <p data-testid="import-summary-partial">
                    {t("import.summary.partialAdded", { count: partialCount })}
                  </p>
                )}
                {errorRowCount > 0 && (
                  <p data-testid="import-summary-errors">
                    {t("import.summary.errorRows", { count: errorRowCount })}
                  </p>
                )}
              </>
            )}
          </div>
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t("import.summary.dismiss")}
            className="text-muted-foreground hover:text-foreground"
            data-testid="import-summary-dismiss"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {result.errors.length > 0 && (
          <div>
            <button
              type="button"
              onClick={onToggleErrors}
              className="inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline"
              data-testid="import-summary-toggle-errors"
            >
              {showErrors ? (
                <ChevronUp className="h-3 w-3" aria-hidden="true" />
              ) : (
                <ChevronDown className="h-3 w-3" aria-hidden="true" />
              )}
              {showErrors
                ? t("import.summary.hideErrors")
                : t("import.summary.showErrors")}
            </button>

            {showErrors && (
              <ul
                className="mt-2 space-y-1 border-l-2 border-current/30 pl-3 text-xs"
                data-testid="import-summary-error-list"
              >
                {result.errors.map((err, idx) => {
                  // MINI_FIX_BL — resolve the parser's i18n key + params
                  // through i18next so TR-locale auditors see TR copy.
                  // Fall back to the English `reason` only when the key
                  // resolves to itself (i.e. no translation found —
                  // i18next returns the input key in that case).
                  //
                  // The cast routes around react-i18next's strongly
                  // typed `t()` overloads — RowError carries a runtime
                  // string from the parser, not one of the literal-
                  // union keys the typed `t` expects.
                  const resolved = (t as (
                    key: string,
                    params?: Record<string, string | number>,
                  ) => string)(err.reasonI18nKey, err.reasonParams ?? {});
                  const reason =
                    resolved === err.reasonI18nKey ? err.reason : resolved;
                  return (
                    <li key={`${err.rowNumber}-${err.field}-${idx}`}>
                      {t("import.errorRow.template", {
                        row: err.rowNumber,
                        field: err.field,
                        reason,
                      })}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </Admonition>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function isXlsxByName(name: string): boolean {
  return name.toLowerCase().endsWith(".xlsx");
}

/**
 * Map a clean `ImportedContract` (parser output, decimal-string shape)
 * into the engagement layer's `ContractInput` shape (numbers in
 * payment/discountRateAnnual, structured `payment` object). Carries
 * `assetClass` through — the engagement layer's
 * `EngagementContractInput` extends `ContractInput` with that field,
 * and the contracts array tolerates the wider shape.
 */
function mapCleanToContractInput(c: ImportedContract): ContractInput {
  const input: ContractInput & { assetClass: AssetClass } = {
    contractId: c.contractId,
    description: c.description,
    commencementDate: c.commencementDate,
    leaseTermMonths: c.leaseTermMonths,
    currency: c.currency,
    functionalCurrency: c.functionalCurrency,
    payment: {
      amount: Number(c.paymentAmount),
      frequency: c.paymentFrequency as PaymentFrequency,
      timing: c.paymentTiming as PaymentTiming,
      // Phase 6e.4.5 — the parser now fills `firstPaymentDate` from
      // the new optional column when present and auto-computes it
      // from (commencement, timing, frequency) otherwise. The
      // commencementDate fallback below is a defensive backstop for
      // a malformed parse, but in the happy path
      // `c.firstPaymentDate` is always populated.
      firstPaymentDate: c.firstPaymentDate ?? c.commencementDate,
    },
    discountRateAnnual: Number(c.discountRate),
    discountRateType: "IBR",
    // MINI_FIX_BM — parser already emits canonical BUILDINGS, the
    // REAL_ESTATE → BUILDINGS rewrite now lives in the parser's
    // `ASSET_CLASS_ALIASES`. No bridge helper needed at the UI layer.
    assetClass: c.assetClass,
  };
  if (c.fxApproach) {
    input.fxApproach = c.fxApproach as ContractFxApproach;
  }
  if (c.commencementSpotRate !== undefined) {
    input.commencementSpotRate = Number(c.commencementSpotRate);
  }
  if (c.variablePaymentsTotal !== undefined) {
    const total = Number(c.variablePaymentsTotal);
    if (Number.isFinite(total) && total > 0) {
      input.variablePaymentExists = true;
    }
  }
  return input;
}

/**
 * Map a `PartialImportedContract` (some valid fields, some missing or
 * invalid) into a best-effort `ContractInput` so the existing
 * status-derivation pipeline can flag it `partial`. Anything the
 * parser couldn't validate is left undefined; Zod will reject on save
 * and the yellow "incomplete" marker surfaces via
 * `deriveContractStatus`.
 *
 * Required-but-missing string fields (e.g. `contractId` when blank)
 * are *intentionally* left as empty strings so the contract still
 * occupies a slot in the list — auditors can open the slot, see what
 * data did make it through, and fix the gaps in place.
 */
function mapPartialToContractInput(
  p: PartialImportedContract,
): ContractInput {
  const input: ContractInput & {
    assetClass?: AssetClass;
  } = {
    contractId: p.contractId ?? "",
    description: p.description,
    commencementDate: p.commencementDate ?? "",
    leaseTermMonths: p.leaseTermMonths ?? 0,
    currency: p.currency ?? "",
    functionalCurrency: p.functionalCurrency,
    payment: {
      amount: p.paymentAmount !== undefined ? Number(p.paymentAmount) : 0,
      frequency: (p.paymentFrequency ?? "MONTHLY") as PaymentFrequency,
      timing: (p.paymentTiming ?? "IN_ARREARS") as PaymentTiming,
      // Phase 6e.4.5 — prefer the parser-derived firstPaymentDate
      // (parser fills it from a column or auto-computes); fall back
      // to commencementDate when even the parser couldn't derive it
      // (commencement was missing too, so the row is partial anyway).
      firstPaymentDate: p.firstPaymentDate ?? p.commencementDate ?? "",
    },
    discountRateAnnual:
      p.discountRate !== undefined ? Number(p.discountRate) : 0,
    discountRateType: "IBR",
    assetClass: p.assetClass,
  };
  if (p.fxApproach) {
    input.fxApproach = p.fxApproach as ContractFxApproach;
  }
  if (p.commencementSpotRate !== undefined) {
    input.commencementSpotRate = Number(p.commencementSpotRate);
  }
  return input;
}

